/**
 * PermissionAttestation — the wizard's required referee-permission confirmation.
 *
 * **This is the CANDIDATE's assertion, not the referee's consent.** The referee has never met this
 * product at the point it renders; they have not opted in to anything and nothing here speaks for
 * them. What it captures is the sender's own record that the candidate says they asked first — the
 * referee's actual consent is taken later, in the referee flow, in their own words.
 *
 * Anatomy follows the shared field idiom (`ui/field.tsx`): a real `<label htmlFor>` so clicking the
 * statement toggles the box, the error wired via `aria-describedby` + `aria-invalid` and announced
 * through `role="alert"`, and the ref forwarded so the wizard can pull focus here when the server
 * refuses. The tick is a native checkbox under `appearance-none` — not a div playing checkbox — so
 * keyboard, form semantics and the required state come for free.
 *
 * Contrast (measured on `surface`, the wizard's card white):
 *  - resting box border `ink-52` 3.40:1 · checked fill `sage-deep` 4.85:1 · error border
 *    `coral-deep` 4.33:1 — all clear of the 3:1 non-text floor (WCAG 1.4.11).
 *  - statement `ink` 15.9:1 (14.4:1 over the error tint) · error text `coral-text` 5.3:1 — AA+.
 *  - the box is 24px, meeting 2.5.8's target minimum on its own; the statement is a far larger one.
 */
import { forwardRef, useId, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

interface PermissionAttestationProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** The statement the candidate is asserting — rendered as the checkbox's label. */
  label: string;
  /** Validation message; when set the control renders its invalid state. */
  error?: string;
}

export const PermissionAttestation = forwardRef<HTMLInputElement, PermissionAttestationProps>(
  function PermissionAttestation({ label, error, id, className, ...props }, ref) {
    const autoId = useId();
    const inputId = id ?? autoId;
    const errorId = `${inputId}-error`;

    return (
      <div className="mt-4">
        <div
          className={cn(
            'flex items-start rounded-md border bg-surface transition-colors duration-ui ease-thread',
            error ? 'border-coral-deep bg-coral-tint/30' : 'border-line-2',
          )}
        >
          <span className="flex-none py-3.5 pl-3.5">
            <span className="relative block h-6 w-6">
              <input
                id={inputId}
                ref={ref}
                type="checkbox"
                // Native `required` is the source of truth for the state; `aria-required` is
                // spelled out alongside it because older screen readers do not map the native
                // attribute on a checkbox. There is no <form> here (the wizard sends via a
                // handler), so neither one can summon a browser validation bubble.
                required
                aria-required="true"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                className={cn(
                  'peer block h-6 w-6 cursor-pointer appearance-none rounded-[8px] border-2 bg-surface',
                  'transition-colors duration-ui ease-thread',
                  'checked:border-sage-deep checked:bg-sage-deep',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
                  // The border doubles as the focus signal on the resting box (3.40:1 → 4.85:1);
                  // in the error state coral has to stay put, so it keeps its border throughout.
                  error ? 'border-coral-deep' : 'border-ink-52 focus-visible:border-sage-deep',
                  className,
                )}
                {...props}
              />
              <svg
                viewBox="0 0 16 16"
                fill="none"
                width={16}
                height={16}
                aria-hidden="true"
                className="pointer-events-none absolute left-1 top-1 text-white opacity-0 transition-opacity duration-ui ease-thread peer-checked:opacity-100"
              >
                <path
                  d="M3 8.4l3.2 3.2L13 4.8"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <label
            htmlFor={inputId}
            className="flex-1 cursor-pointer py-3.5 pl-3 pr-3.5 font-body text-[13px] font-medium leading-relaxed text-ink"
          >
            {label}
          </label>
        </div>
        {error && (
          <p id={errorId} role="alert" className="mt-1.5 text-[11.5px] font-medium leading-snug text-coral-text">
            {error}
          </p>
        )}
      </div>
    );
  },
);
